<template>
  <SogPage
    id="page"
    title="Bestandsübersicht"
  >
    <!--
    Store List
-->
    <SogCard v-show="!_type">
      <SogCardActions :is-toolbar="true">
        <SogCardActionButton
          :kind="SogButtonKind.Refresh"
          @click="p_init"
        />
        <SogCardActionButton :kind="SogButtonKind.Back" />
      </SogCardActions>
      <SogCardContent v-show="_items.length===0">
        Sie haben keine Produkte in Bestand
      </SogCardContent>
      <SogCardContent
        v-for="item in _items"
        :key="item"
        :item="item"
      >
        <StoreItemCardComponent :item="item">
          <SogCardActions v-show="isInsurance(item)||isInsuranceExportBlank(item)">
            <SogCardActionButton
              mdi-icon="mdi-list-box"
              tooltip="Kartenübersicht"
              @click="onList(item)"
            />
          </SogCardActions>
        </StoreItemCardComponent>
      </SogCardContent>
    </SogCard>
    <!--
    Insurance
-->
    <SogCard v-show="_type==='insurance'">
      <SogCardTitle>
        {{ _storeStore?.currentItem?.productName }}
      </SogCardTitle>
      <SogCardActions :is-toolbar="true">
        <SogCardActionButton :kind="SogButtonKind.Back" />
      </SogCardActions>
      <SogCardContent v-show="_insurances.length===0">
        Sie haben keine Versicherungskarte in Bestand
      </SogCardContent>
      <SogCardContent
        v-for="insurance in _insurances"
        :key="insurance"
      >
        <StoreItemInsuranceCardComponent :insurance="insurance" />
      </SogCardContent>
    </SogCard>
    <!--
    InsuranceExportBlanks
-->
    <SogCard v-show="_type==='exportblank'">
      <SogCardTitle>
        {{ _storeStore?.currentItem?.productName }}
      </SogCardTitle>
      <SogCardActions :is-toolbar="true">
        <SogCardActionButton
          :kind="SogButtonKind.Refresh"
          @click="refresh"
        />
        <SogCardActionButton :kind="SogButtonKind.Back" />
      </SogCardActions>
      <SogCardContent v-show="_insuranceExportBlanks.length===0">
        Sie haben keine Ausfuhrversicherungsformulare in Bestand
      </SogCardContent>
      <SogCardContent
        v-for="blank in _insuranceExportBlanks"
        :key="blank"
      >
        <StoreItemInsuranceExportBlankCardComponent :insurance-export-blank="blank">
          <SogCardActions>
            <SogCardActionButton
              mdi-icon="mdi-information"
              @click="onDetailsClicked(blank)"
            />
            <SogCardActionButton
              v-show="!(blank.status)"
              mdi-icon="mdi-pencil-off"
              tooltip="als falsche ausgefüllt markieren"
              @click="onIncorrectlyFilledClicked(blank)"
            />
            <SogCardActionButton
              v-show="blank.status===InsuranceExportBlankStatus.IncorrectlyFilled"
              mdi-icon="mdi-pencil"
              tooltip="als gültig markieren"
              @click="onRemoveIncorrectlyFilledClicked(blank)"
            />
          </SogCardActions>
        </StoreItemInsuranceExportBlankCardComponent>
      </sogcardcontent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">
import SogPage from "@/shared/ui/SogPage.vue";
import {onMounted, reactive, ref} from "vue";
import {onBeforeRouteUpdate, RouteLocationNormalized, useRoute, useRouter} from "vue-router";
import {useSpinner} from "@/shared/composables/SogSpinner";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import apiStore from "@/modules/Store/api";
import StoreItem from "@/shared/model/StoreItem";
import StoreItemCardComponent from "@/modules/Store/components/StoreItemCardComponent.vue";
import {ProductType} from "@/shared/model/productType";
import {useStoreStore} from "@/modules/Store/store";
import Insurance from "@/shared/model/Insurance";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import StoreItemInsuranceCardComponent from "@/modules/Store/components/StoreItemInsuranceCardComponent.vue";
import InsuranceExportBlank, {InsuranceExportBlankStatus} from "@/shared/model/InsuranceExportBlank";
import StoreItemInsuranceExportBlankCardComponent
  from "@/modules/Store/components/StoreItemInsuranceExportBlankCardComponent.vue";
import apiInsuranceExportBlank from "@/modules/InsuranceExportBlank/api";
import {useNavigationStore} from "@/modules/Navigation/store";
import {IdType} from "@/shared/model/idType";
import {compareStrings} from "@/shared/utils/StringUtils";


const _spinner = useSpinner()
const _items = reactive<StoreItem[]>([])
const _insurances = reactive<Insurance[]>([])
const _insuranceExportBlanks = reactive<InsuranceExportBlank[]>([])
const _type = ref<string | undefined>(undefined)
const _storeStore = useStoreStore()
const _router = useRouter()
const _route = useRoute();
const _navigationStore = useNavigationStore();

async function p_init(to?: RouteLocationNormalized) {
  _type.value = to?.params.type as string

  _spinner.show()
  _items.length = 0;
  if (!_type.value) {
    _storeStore.currentItem = undefined;
  }
  try {
    switch (_type.value) {
      case 'insurance':
        if (!_storeStore.currentItem) {
          _router.back()
          return
        }
        _insurances.length = 0;
        const resInsurance = await apiStore.listInsurance(_storeStore.currentItem.purchaseProductPrice, _storeStore.currentItem.grossPrice)
        if (resInsurance)
          _insurances.push(...resInsurance)
        break;
      case 'exportblank':
        _insuranceExportBlanks.length = 0;
        const resExportBlank = await apiStore.listInsuranceExportBlank()
        if (resExportBlank)
          _insuranceExportBlanks.push(...resExportBlank.sort((a, b) => compareStrings(a.blankNumber, b.blankNumber)))
        break;
      default:
        const resStore = await apiStore.listStore()
        if (resStore) {
          _items.push(...resStore)
        }
        break;
    }


  } finally {
    _spinner.hide()
  }
}

onMounted(async () => {
  const route = useRoute()
  await p_init(route)
})

onBeforeRouteUpdate(async (to) => {
  await p_init(to)
});

async function onList(item: StoreItem) {
  _storeStore.currentItem = item
  const path = "/store/list/" + (isInsurance(item) ? "insurance" : (isInsuranceExportBlank(item) ? "exportblank" : ""));
  await _router.push(path);
}

function isInsurance(item: StoreItem) {
  return item.productType === ProductType.Kzk || item.productType === ProductType.Tz || item.productType === ProductType.Zoll;
}

function isInsuranceExportBlank(item: StoreItem) {
  return item.productType === ProductType.ExportBlank;
}

async function refresh() {
  await p_init(_route);
}

async function onIncorrectlyFilledClicked(blank: InsuranceExportBlank) {
  _spinner.show()
  await apiInsuranceExportBlank.setStatus(blank.id, InsuranceExportBlankStatus.IncorrectlyFilled);
  await refresh()
  _spinner.hide()
}

async function onRemoveIncorrectlyFilledClicked(blank: InsuranceExportBlank) {
  _spinner.show()
  await apiInsuranceExportBlank.setStatus(blank.id, undefined);
  await refresh()
  _spinner.hide()
}

async function onDetailsClicked(blank: InsuranceExportBlank) {
  await _navigationStore.showObject(IdType.InsuranceExportBlank, blank.id)
}

</script>
<style scoped>
.test {
}
</style>
